import type { IApiContentResponseModel, PaymentMethodsPropertiesModel } from '@/services/platform/api/model';
import { ImageMap } from '@/lib/core/utils/image';
import { CheckoutPaymentMethodTypes } from '@/services/platform/data/checkout';
import type { MethodLogo } from './types';

/**
 * CMS `paymentMethods` slugs.
 *
 * These are authored in Umbraco, so they are matched case-insensitively and with `-`/`_` treated as
 * equivalent — an editor typing `mb-way` should not silently drop the row.
 */
export const METHOD_SLUGS = {
  savedCard: 'saved-card',
  card: 'credit-card',
  wallet: 'wallet',
  mbWay: 'mb-way',
} as const;

export type MethodSlug = (typeof METHOD_SLUGS)[keyof typeof METHOD_SLUGS];

/**
 * Card-brand logo geometry, taken from Figma node 2364:69688 (the accepted-cards row) and reused
 * verbatim by `card-brands.tsx`. Kept in sync deliberately rather than imported — that module keeps
 * its list private and shapes it for a marketing strip, not for a row tile.
 */
const CARD_BRAND_LOGOS: MethodLogo[] = [
  { src: '/images/cards/visa.svg', alt: 'Visa', width: 24.5, height: 7.82 },
  { src: '/images/cards/mastercard.svg', alt: 'Mastercard', width: 22.5, height: 13.81 },
  { src: '/images/cards/maestro.svg', alt: 'Maestro', width: 22.5, height: 13.81 },
  {
    src: '/images/cards/amex.svg',
    alt: 'American Express',
    width: 28.19,
    height: 7.26,
    background: '#1f72cd',
  },
];

/** Wallet and MB WAY marks, exported from Figma node 1716:92988 (the payment-methods list). */
const WALLET_LOGOS: MethodLogo[] = [
  { src: '/images/payments/apple-pay.svg', alt: 'Apple Pay', width: 25.07, height: 10.42 },
  { src: '/images/payments/google-pay.svg', alt: 'Google Pay', width: 24.69, height: 9.7 },
];

const MBWAY_LOGOS: MethodLogo[] = [
  { src: '/images/payments/mbway.png', alt: 'MB WAY', width: 28, height: 17.36 },
];

/**
 * Logos to draw when the CMS has nothing authored for a method.
 *
 * The `checkout` document type exists in Umbraco but its `paymentMethods` nodes may not be filled
 * in yet, and a payment screen with unlabelled blank rows is worse than one showing the brands from
 * the design. Same reasoning as `FALLBACK_BRANDS` in `card-brands.tsx`.
 */
export const FALLBACK_LOGOS: Record<MethodSlug, MethodLogo[]> = {
  [METHOD_SLUGS.savedCard]: [],
  [METHOD_SLUGS.card]: CARD_BRAND_LOGOS,
  [METHOD_SLUGS.wallet]: WALLET_LOGOS,
  [METHOD_SLUGS.mbWay]: MBWAY_LOGOS,
};

/** A single saved card's brand tile, so the row can show just that brand rather than all four. */
export function logoForCardBrand(brand?: string | null): MethodLogo[] {
  if (!brand) return [];
  const normalised = brand.toLowerCase().replace(/[\s_]/g, '');
  const match = CARD_BRAND_LOGOS.find(logo => {
    const alt = logo.alt.toLowerCase().replace(/[\s_]/g, '');
    return alt === normalised || (normalised === 'amex' && alt === 'americanexpress');
  });
  return match ? [match] : [];
}

function normaliseSlug(slug?: string): string {
  return (slug ?? '').trim().toLowerCase().replace(/_/g, '-');
}

/**
 * Indexes the CMS-authored `paymentMethods` blocks by slug so a row can look up its logos.
 *
 * Blocks with no slug are skipped rather than guessed at — an unlabelled block cannot be matched to
 * a payment method, and inventing a mapping from its position would break the moment an editor
 * reorders them.
 */
export function indexCmsMethods(
  paymentMethods?: IApiContentResponseModel[] | null
): Map<string, MethodLogo[]> {
  const index = new Map<string, MethodLogo[]>();

  for (const block of paymentMethods ?? []) {
    const properties = block?.properties as PaymentMethodsPropertiesModel | undefined;
    const slug = normaliseSlug(properties?.slug);
    if (!slug) continue;

    const logos = (properties?.images ?? [])
      .map(image => ImageMap(image))
      .filter((image): image is NonNullable<typeof image> => !!image?.src)
      .map<MethodLogo>(image => ({
        src: image.src as string,
        alt: image.alt ?? '',
        // CMS images have no design-measured geometry, so they are sized by height and left to
        // find their own width — the tile never stretches them.
        width: 0,
        height: 24,
      }));

    index.set(slug, logos);
  }

  return index;
}

/** CMS logos when authored, the design's fallback set otherwise. */
export function resolveLogos(slug: MethodSlug, cmsIndex: Map<string, MethodLogo[]>): MethodLogo[] {
  const cms = cmsIndex.get(slug);
  return cms && cms.length > 0 ? cms : FALLBACK_LOGOS[slug];
}

/**
 * Whether the server offers a method for this purchase.
 *
 * `paymentMethodTypes` comes from `GET /me/checkout/config` and is authoritative — MB WAY is only
 * offered for EUR, for instance. An absent or empty list means the config call failed or the
 * account is misconfigured; treating that as "card only" keeps the screen usable instead of
 * rendering nothing.
 */
export function serverOffers(types: string[] | null | undefined, type: string): boolean {
  if (!types || types.length === 0) return type === CheckoutPaymentMethodTypes.Card;
  return types.some(candidate => candidate?.toLowerCase() === type);
}
